import { useEffect, useState, type FormEvent } from 'react';
import { format, isBefore, startOfDay } from 'date-fns';
import { supabase } from '../lib/supabase';
import { errorMessage } from '../lib/format';
import {
  dateKey,
  planHolidayShift,
  type SessionShift,
  type ShiftableSession,
} from '../lib/schedule';
import type { Holiday } from '../lib/types';
import type { Recipient } from '../lib/email';
import { Modal } from './Modal';
import { EmailComposeModal } from './EmailComposeModal';
import { useToast } from './Toast';

// Add (or remove) a no-class day. Used from the calendar (click a day)
// and from Settings (+ Add no-class day). Adding one slides affected
// class series one week later (lib/schedule.planHolidayShift); the dialog
// previews what will move, and — after saving — offers to email the
// clients in each affected class about the closure.

interface AffectedClass {
  classId: string;
  label: string;
  count: number; // sessions that moved
  movedTo: string | null; // new ISO start of the session that was on this day
  recipients: Recipient[]; // enrolled clients with an email
  skipped: number; // enrolled clients without an email
}

interface Planned {
  affected: AffectedClass[];
  shifts: SessionShift[];
}

export function NoClassDayDialog({
  fixedDay,
  holiday,
  onClose,
  onChanged,
}: {
  fixedDay?: Date | null;
  holiday?: Holiday | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [dateStr, setDateStr] = useState(fixedDay ? format(fixedDay, 'yyyy-MM-dd') : '');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [planned, setPlanned] = useState<Planned | null>(null);

  // Two-step flow: compose the no-class day, then (after saving) notify.
  const [phase, setPhase] = useState<'compose' | 'notify'>('compose');
  const [saved, setSaved] = useState(false);
  const [notifyingClass, setNotifyingClass] = useState<AffectedClass | null>(null);
  const [notified, setNotified] = useState<Set<string>>(new Set());

  const day = dateStr ? new Date(`${dateStr}T00:00`) : null;
  const isPast = day ? isBefore(day, startOfDay(new Date())) : false;

  useEffect(() => {
    setPlanned(null);
    setErr(null);
    if (!dateStr || holiday) return;
    const dayStart = new Date(`${dateStr}T00:00`);
    if (Number.isNaN(dayStart.getTime())) return;
    if (isBefore(dayStart, startOfDay(new Date()))) {
      setPlanned({ affected: [], shifts: [] });
      return;
    }
    let active = true;
    (async () => {
      // 1. Scheduled sessions on this exact day…
      const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
      const { data: hits, error: e1 } = await supabase
        .from('class_sessions')
        .select('id, class_id, classes(title, class_types(name))')
        .eq('status', 'scheduled')
        .gte('starts_at', dayStart.toISOString())
        .lt('starts_at', dayEnd.toISOString());
      if (!active) return;
      if (e1) {
        setErr(errorMessage(e1));
        return;
      }
      const classIds = [...new Set((hits ?? []).map((h) => h.class_id as string))];
      if (classIds.length === 0) {
        setPlanned({ affected: [], shifts: [] });
        return;
      }

      // 2. …every upcoming session of those classes (the slide can ripple
      //    past this date), all holidays for cascades, and the rosters.
      const [futureRes, holRes, enrRes] = await Promise.all([
        supabase
          .from('class_sessions')
          .select('id, class_id, starts_at, ends_at')
          .in('class_id', classIds)
          .eq('status', 'scheduled')
          .gte('starts_at', dayStart.toISOString()),
        supabase.from('holidays').select('holiday_date'),
        supabase
          .from('enrollments')
          .select('class_id, clients(first_name, email)')
          .in('class_id', classIds),
      ]);
      if (!active) return;
      if (futureRes.error) {
        setErr(errorMessage(futureRes.error));
        return;
      }
      const future = (futureRes.data ?? []) as ShiftableSession[];
      const otherHolidays = new Set(
        ((holRes.data ?? []) as { holiday_date: string }[]).map((h) => h.holiday_date),
      );
      const shifts = planHolidayShift(future, dateKey(dayStart), otherHolidays);

      const labelByClass = new Map<string, string>();
      const hitSessionByClass = new Map<string, string>();
      for (const h of hits ?? []) {
        const cid = h.class_id as string;
        if (!hitSessionByClass.has(cid)) hitSessionByClass.set(cid, h.id as string);
        const c = h.classes as unknown as {
          title: string | null;
          class_types: { name: string } | null;
        } | null;
        labelByClass.set(cid, c?.title || c?.class_types?.name || 'Class');
      }
      const countByClass = new Map<string, number>();
      const idToClass = new Map(future.map((s) => [s.id, s.class_id]));
      const newStartById = new Map(shifts.map((s) => [s.id, s.starts_at]));
      for (const s of shifts) {
        const cid = idToClass.get(s.id);
        if (cid) countByClass.set(cid, (countByClass.get(cid) ?? 0) + 1);
      }

      // Rosters → recipients (with email) + skipped counts, per class.
      const recByClass = new Map<string, Recipient[]>();
      const skipByClass = new Map<string, number>();
      for (const row of enrRes.data ?? []) {
        const cid = row.class_id as string;
        const client = row.clients as unknown as {
          first_name: string;
          email: string | null;
        } | null;
        if (!client) continue;
        if (client.email) {
          const arr = recByClass.get(cid) ?? [];
          arr.push({ email: client.email, name: client.first_name });
          recByClass.set(cid, arr);
        } else {
          skipByClass.set(cid, (skipByClass.get(cid) ?? 0) + 1);
        }
      }

      setPlanned({
        affected: classIds.map((cid) => ({
          classId: cid,
          label: labelByClass.get(cid) ?? 'Class',
          count: countByClass.get(cid) ?? 0,
          movedTo: newStartById.get(hitSessionByClass.get(cid) ?? '') ?? null,
          recipients: recByClass.get(cid) ?? [],
          skipped: skipByClass.get(cid) ?? 0,
        })),
        shifts,
      });
    })();
    return () => {
      active = false;
    };
  }, [dateStr, holiday]);

  async function addHoliday(e: FormEvent) {
    e.preventDefault();
    if (!dateStr || !planned) return;
    setBusy(true);
    setErr(null);

    const { error: insErr } = await supabase
      .from('holidays')
      .insert({ holiday_date: dateStr, label: label.trim() || 'No classes' });
    if (insErr) {
      setBusy(false);
      setErr(
        insErr.code === '23505'
          ? 'That day is already marked as a no-class day.'
          : errorMessage(insErr),
      );
      return;
    }

    let failed = 0;
    for (const s of planned.shifts) {
      const { error } = await supabase
        .from('class_sessions')
        .update({ starts_at: s.starts_at, ends_at: s.ends_at })
        .eq('id', s.id);
      if (error) failed++;
    }

    setBusy(false);
    setSaved(true);
    if (failed > 0) {
      toast.error(`No-class day added, but ${failed} session move(s) failed — check the calendar.`);
    } else if (planned.shifts.length > 0) {
      toast.success(
        `No-class day added — ${planned.shifts.length} session${planned.shifts.length === 1 ? '' : 's'} moved a week later.`,
      );
    } else {
      toast.success('No-class day added.');
    }

    // If any classes were affected, offer to email their clients;
    // otherwise there's nothing to notify, so just finish.
    if (planned.affected.length > 0) setPhase('notify');
    else onChanged();
  }

  async function removeHoliday() {
    if (!holiday) return;
    setBusy(true);
    const { error } = await supabase.from('holidays').delete().eq('id', holiday.id);
    setBusy(false);
    if (error) setErr(errorMessage(error));
    else {
      toast.success('No-class day removed. (Previously moved classes stay where they are.)');
      onChanged();
    }
  }

  // Closing via backdrop/Esc/X: reload the parent if we already saved.
  const handleClose = () => (saved ? onChanged() : onClose());

  function cancellationEmail(a: AffectedClass) {
    if (!day) return { subject: '', message: '' };
    const reason = label.trim() || 'a holiday';
    const dayLong = format(day, 'EEEE, MMMM d');
    const moved = a.movedTo ? new Date(a.movedTo) : null;
    const subject = `${a.label} — no class on ${format(day, 'EEE MMM d')}`;
    const message = moved
      ? `Heads up: the academy is closed on ${dayLong} (${reason}), so there's no ${a.label} class that day.\n\nThat session has been moved to ${format(
          moved,
          'EEEE, MMMM d',
        )} at ${format(moved, 'h:mm a')} — see you then!`
      : `Heads up: the academy is closed on ${dayLong} (${reason}), so there's no ${a.label} class that day. We'll see you at your next scheduled session.`;
    return { subject, message };
  }

  // ── Existing holiday → offer removal ──
  if (holiday && fixedDay) {
    return (
      <Modal title={format(fixedDay, 'EEEE, MMMM d, yyyy')} onClose={onClose}>
        <p className="muted" style={{ marginTop: 0 }}>
          This day is marked <b>🚫 {holiday.label}</b> — no classes are scheduled on it.
        </p>
        <p className="muted small">
          Removing it won't move any classes back; it just allows classes on this day again.
        </p>
        {err && <p className="error-text">{err}</p>}
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: '0.8rem' }}>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button className="btn btn-danger" onClick={() => void removeHoliday()} disabled={busy}>
            {busy ? 'Removing…' : 'Remove no-class day'}
          </button>
        </div>
      </Modal>
    );
  }

  // ── Emailing one affected class → swap in the compose dialog ──
  if (notifyingClass) {
    const { subject, message } = cancellationEmail(notifyingClass);
    return (
      <EmailComposeModal
        title={`Cancellation email — ${notifyingClass.label}`}
        recipients={notifyingClass.recipients}
        skippedNoEmail={notifyingClass.skipped}
        defaultSubject={subject}
        defaultMessage={message}
        sendLabel="Send cancellation email"
        onClose={() => setNotifyingClass(null)}
        onSent={() => {
          setNotified((prev) => new Set(prev).add(notifyingClass.classId));
          setNotifyingClass(null);
        }}
      />
    );
  }

  // ── Notify step: list the affected classes with an email button each ──
  if (phase === 'notify' && planned) {
    return (
      <Modal title="Email affected classes" onClose={handleClose}>
        <p className="muted small" style={{ marginTop: 0 }}>
          {day && <b>🚫 {format(day, 'EEE MMM d')}</b>} is now a no-class day
          {planned.shifts.length > 0
            ? ` — ${planned.shifts.length} session${planned.shifts.length === 1 ? '' : 's'} moved a week later.`
            : '.'}{' '}
          Let the affected classes know:
        </p>
        <div style={{ marginTop: '0.4rem' }}>
          {planned.affected.map((a) => (
            <div className="roster-row" key={a.classId}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{a.label}</div>
                <div className="muted small">
                  {a.recipients.length} to email
                  {a.skipped > 0 ? ` · ${a.skipped} without an email` : ''}
                  {a.movedTo ? ` · now ${format(new Date(a.movedTo), 'EEE MMM d')}` : ''}
                </div>
              </div>
              {notified.has(a.classId) ? (
                <span className="badge badge-success">✓ Sent</span>
              ) : (
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={a.recipients.length === 0}
                  title={
                    a.recipients.length === 0
                      ? 'No enrolled clients have an email on file'
                      : 'Email this class about the closure'
                  }
                  onClick={() => setNotifyingClass(a)}
                >
                  Send cancellation email
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button className="btn btn-primary" onClick={() => onChanged()}>
            Done
          </button>
        </div>
      </Modal>
    );
  }

  // ── Compose step: pick the day + preview what shifts ──
  return (
    <Modal
      title={fixedDay ? format(fixedDay, 'EEEE, MMMM d, yyyy') : 'Add a no-class day'}
      onClose={handleClose}
    >
      <form onSubmit={addHoliday}>
        <p className="muted small" style={{ marginTop: 0 }}>
          Mark a <b>no-class day</b> (holiday, vacation, arena closed…). Any classes that land on it
          slide one week later, automatically.
        </p>
        {!fixedDay && (
          <div className="field">
            <label htmlFor="ncd-date">Date</label>
            <input
              id="ncd-date"
              type="date"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              required
              autoFocus
            />
          </div>
        )}
        <div className="field">
          <label htmlFor="ncd-label">Reason</label>
          <input
            id="ncd-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Thanksgiving"
            autoFocus={Boolean(fixedDay)}
          />
        </div>

        {!dateStr ? null : isPast ? (
          <p className="muted small">This date is in the past, so no classes will be moved.</p>
        ) : planned == null ? (
          <p className="muted small">Checking which classes are affected…</p>
        ) : planned.affected.length === 0 ? (
          <p className="muted small">
            No scheduled classes fall on this day — nothing needs to move.
          </p>
        ) : (
          <div className="date-preview" style={{ marginBottom: '0.9rem' }}>
            <strong>These classes slide one week later:</strong>
            <ul style={{ gridTemplateColumns: '1fr' }}>
              {planned.affected.map((a) => (
                <li key={a.classId}>
                  {a.label} — {a.count} session{a.count === 1 ? '' : 's'} move
                </li>
              ))}
            </ul>
            <p className="muted small" style={{ margin: '0.4rem 0 0' }}>
              Each series keeps its weekly rhythm and finishes a week later. After you add the day,
              you can email each class about the closure.
            </p>
          </div>
        )}

        {err && <p className="error-text">{err}</p>}
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: '0.6rem' }}>
          <button type="button" className="btn btn-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || !dateStr || (!isPast && planned == null)}
          >
            {busy ? 'Saving…' : 'Add no-class day'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
